import { createIntegration, MethodType } from '@endeavour/payment-integration';
import { getMethods } from './api/get-methods.js';
import { methods } from './methods/deposit/index.js';
import { methods as methods$1 } from './methods/withdrawal/index.js';
import { createMethodMap, isMatchingId } from './util.js';
import { processPayment } from './api/process-payment.js';
import { cancelWithdrawal } from './api/cancel-withdrawal.js';
import { createHostedField } from './hosted/create-hosted-field.js';
import { transform, transformEncryptedCardNumber, transformExpirationDate, transformEncryptedCVV } from './transforms.js';
import { getSchemaFromFields } from './schema.js';

const INTEGRATION_ID = 'devcode';
const createDevcodeIntegration = (devcodeConfig) => {
    let sessionId = null;
    let sessionTime = null;
    let getSession = null;
    function getCurrentSessionId() {
        if (!getSession) {
            throw new Error('[Devcode] No session provider');
        }
        if (!sessionId || !sessionTime || sessionTime + devcodeConfig.sessionTimeout < Date.now()) {
            return getSession(INTEGRATION_ID).then((session) => {
                sessionId = session.sessionId;
                sessionTime = Date.now();
                return sessionId;
            });
        }
        return Promise.resolve(sessionId);
    }
    return createIntegration({
        name: INTEGRATION_ID,
        async initialize(getIntegrationSession) {
            getSession = getIntegrationSession;
        },
        async destroy() {
            sessionId = null;
            sessionTime = null;
            getSession = null;
        },
        async getMethods({ configuration, methods, type }) {
            const sessionId = await getCurrentSessionId();
            const devcodeMethods = await getMethods({
                apiUrl: devcodeConfig.apiUrl,
                merchantId: devcodeConfig.merchantId,
            }, {
                userProfileId: configuration.userProfileId,
                sessionId: sessionId,
                type: type === MethodType.DEPOSIT ? 'deposit' : 'withdrawal',
            });
            const devcodeMethodMap = createMethodMap(devcodeMethods);
            return methods
                .filter((method) => !!method && method.integrationId === INTEGRATION_ID)
                .map((method) => {
                const foundMethod = devcodeMethodMap[method.providerId.toLowerCase()];
                return {
                    ...method,
                    min: foundMethod?.limit?.min
                        ? parseFloat(foundMethod.limit.min)
                        : method.min,
                    max: foundMethod?.limit?.max
                        ? parseFloat(foundMethod.limit.max)
                        : method.max,
                    providerData: foundMethod,
                };
            });
        },
        async getSavedAccounts({ configuration, methods, type }) {
            return methods.flatMap((method) => {
                const { providerData } = method;
                if (!providerData) {
                    return [];
                }
                if (!providerData.accounts || providerData.accounts.length == 0) {
                    return [];
                }
                return providerData.accounts
                    .filter(({ expired }) => {
                    return !expired;
                })
                    .map((account) => ({
                    name: account.maskedAccount,
                    accountId: account.accountId,
                    method,
                }));
            });
        },
        async getMethodDetailsForm({ providerId, accountId, type }) {
            const methods$2 = type === MethodType.DEPOSIT ? methods : methods$1;
            const foundMethod = methods$2.find((method) => {
                return isMatchingId(providerId, method);
            });
            if (!foundMethod) {
                return { fields: [] };
            }
            const fields = (await foundMethod.fields()).filter(({ validation: { repeatPayment: { visible: repeatVisible }, newPayment: { visible: newVisible }, }, }) => (accountId ? repeatVisible : newVisible));
            return {
                fields,
                schema: getSchemaFromFields(fields, !!accountId),
            };
        },
        async processPayment({ configuration, providerId, accountId, type, formData }) {
            const currentSessionId = await getCurrentSessionId();
            const methods$2 = type === MethodType.DEPOSIT ? methods : methods$1;
            const foundMethod = methods$2.find((method) => {
                return isMatchingId(providerId, method);
            });
            const method = foundMethod?.providerType || providerId;
            const transformedFormData = transform(formData, [
                transformEncryptedCardNumber,
                transformExpirationDate,
                transformEncryptedCVV,
            ]);
            const response = await processPayment({
                apiUrl: devcodeConfig.apiUrl,
                merchantId: devcodeConfig.merchantId,
            }, {
                userProfileId: configuration.userProfileId,
                sessionId: currentSessionId,
                type: type === MethodType.DEPOSIT ? 'deposit' : 'withdrawal',
                formData: transformedFormData,
                accountId,
                method: method.toLowerCase(),
            });
            return {
                success: response.success,
                redirectOutput: response.redirectOutput,
                transactionId: currentSessionId,
                error: response.success ? undefined : response.statusCode,
            };
        },
        getHostedField({ providerId, placeholder, name }) {
            const foundMethod = [...methods, ...methods$1].find((method) => {
                return isMatchingId(providerId, method);
            });
            const method = foundMethod?.service || providerId;
            return createHostedField({
                name,
                merchantId: devcodeConfig.merchantId,
                hostedFieldUrl: devcodeConfig.hostedFieldUrl,
                service: method,
                placeholder,
            });
        },
        getPendingWithdrawals() { },
        async cancelWithdrawal({ configuration, transactionId }) {
            const sessionId = await getCurrentSessionId();
            return cancelWithdrawal({
                apiUrl: devcodeConfig.apiUrl,
                merchantId: devcodeConfig.merchantId,
            }, {
                userProfileId: configuration.userProfileId,
                sessionId: sessionId,
                transactionId,
            });
        },
    });
};

export { INTEGRATION_ID, createDevcodeIntegration };
